/**
 * InstanceSwitcher — manages multiple workspaces (businesses/initiatives), each with its own goals, context, leads and brand.
 * Creating a workspace runs the full onboarding flow. Composition: workspaces/useInstances + WorkspaceDropdown + WorkspaceModals.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import OnboardingFlow from "./onboarding/OnboardingFlow";
import { useInstances } from "./workspaces/useInstances";
import { WorkspaceDropdown } from "./workspaces/WorkspaceDropdown";
import { NewWorkspaceModal, WorkspaceSettingsModal } from "./workspaces/WorkspaceModals";

const InstanceSwitcher = ({ userId, currentInstance, onInstanceChange }) => {
  const data = useInstances({ userId, currentInstance, onInstanceChange });
  const [isExpanded, setIsExpanded] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [pendingInstance, setPendingInstance] = useState(null);   // instance currently going through onboarding
  const [settingsInstance, setSettingsInstance] = useState(null);

  const startOnboarding = (instance) => {
    setPendingInstance(instance);
    setIsExpanded(false);
  };

  const handleSwitch = async (instance) => {
    if (!instance.onboardingComplete) return startOnboarding(instance);
    if (await data.switchInstance(instance)) setIsExpanded(false);
  };

  const handleCreate = async (form) => {
    if (!form.name.trim()) return toast.error("Please enter a name for this workspace");
    const created = await data.createInstance(form);
    if (!created) return;
    setShowNameModal(false);
    startOnboarding(created);
  };

  const handleOnboardingComplete = async () => {
    const completed = await data.resolveCompleted(pendingInstance);
    setPendingInstance(null);
    data.upsertInstance(completed);
    onInstanceChange(completed);
  };

  const handleDelete = async (instanceId) => {
    if (await data.deleteInstance(instanceId)) setSettingsInstance(null);
  };

  if (data.loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading...</span>
      </div>
    );
  }

  return (
    <>
      <WorkspaceDropdown instances={data.instances} currentInstance={currentInstance} isExpanded={isExpanded}
        onToggle={() => setIsExpanded((v) => !v)} onSwitch={handleSwitch} onOpenSettings={setSettingsInstance}
        onCreate={() => { setShowNameModal(true); setIsExpanded(false); }} />

      <NewWorkspaceModal open={showNameModal} onOpenChange={setShowNameModal} creating={data.creating} onCreate={handleCreate} />

      {pendingInstance && (
        <OnboardingFlow userId={userId} instanceId={pendingInstance.instanceId} instanceName={pendingInstance.name} closable
          onClose={() => setPendingInstance(null)} onComplete={handleOnboardingComplete} />
      )}

      <WorkspaceSettingsModal open={!!settingsInstance} onOpenChange={(open) => !open && setSettingsInstance(null)}
        instance={settingsInstance} onDelete={handleDelete} />
    </>
  );
};

export default InstanceSwitcher;
